import type { Law, Campaign } from './types';
import {STOPS} from '../content/economy';
/**
 * Alle Spielzüge, die ein Spieler auslösen kann. Die UI ruft ausschließlich diese Funktionen auf
 * und liest danach den Zustand neu. Jede Aktion ist deterministisch bei gleichem Seed, damit
 * Partien als Zugfolge gespeichert, wiederholt und später zwischen Spielern synchronisiert werden können.
 */
import { S, setS, fresh } from './state';
import { rngState, restoreRng } from './rng';
import { runElection, isWon } from './election';
import { startGovern, tick, introduce } from './govern';
import { blocker } from './budget';
import { decideOpp } from './media';
import { appoint, scandalDecide, candidates, defaultRess, formCabinet } from './ministers';
import { budgetCabinet } from './budget';
import {startCampaign,finishCampaign,duelEffect,stopsUsed} from './campaign';
import { decideCrisis } from './crises';
import { decidePtag, trySign } from './party';
import { startRepeal } from './repeal';
import { customDefault, newBlock } from './custom';
import { draftInfo } from './ministers';
import { TPL, ISSUES } from '../content/issues';
import { PRESETS } from '../content/settings';
import { initStates } from '../content/states';
import { AX } from '../content/axes';
import { initInd } from './indicators';
import { addBestand } from './repeal';
import { CF } from '../content/settings';
import { log } from './state';
import { spendingFreeze, budgetRetry } from './fiscal';
import { coopGov, canDraft, issueDraftable, startCoord, coordReply, p2Propose, myPartnerPid, ownsLaw } from './coalition';
import { ISS } from '../content/states';

/** Erster Ansatz eines Problems, den diese Partei beauftragen darf (Ressortprinzip). */
const firstTpl = (party: string, iss: string) => Math.max(0, ISS[iss].laws.findIndex((T: any) => canDraft(party, T)));
import { versusStart, versusCreate, versusOnPick, coalReply, versusTalksOk, versusSignOk, oppPropose, oppSue, oppAttack, oppMisstrauen, partnerBlock, partnerQuit, p2Vote, p2Plan, versusOnTalks, versusContractChanged, p2Wish, p2RessWish, isVersus, setActor, actor, timeRole, type Role } from './versus';
import { mandateReturn, aiAfterReply } from './slots';
import { mpCampStart, mpTheme, mpStep, mpStop, mpStopsReset, mpAnswer, mpCampDone, mpAd } from './mpcamp';
import { setAlloc } from './ads';
import { aiTurn } from './autopilot';
import { brDeal } from './parliament';
import { decide } from './decisions';
import { ROLES } from './versus';

/** Bis zu sechs Monate weiter, stoppt bei allem, was eine Entscheidung braucht. */
function runMonths() { S.event = null; for (let i = 0; i < 6; i++) { aiTurn(); if (blocker() || tick() || S.phase !== 'govern') break; } aiTurn(); if (S.mp) S.mp.ready = {}; }
import { isHex, colorProblem } from './colors';
import { openReview, reviewDone, reposition } from './review';

import { RULES, verOf } from './version';
import { lawFx, openAgendaDraft } from './v2';
import { AGENDA } from '../content/agenda';
import { upgradeRules } from './upgrade';
import { admSet, admInv, admLand, admOpt } from './adm';
import { comFocus, comLaw, comTalk } from './com';
import { oppMove, coalCommittee } from './opp5';
import { allAxes } from './axes';
import { allIssues } from './axes';

export type Action = { type: string; [k: string]: any };

/** Zugprotokoll für Replays und spätere Synchronisation. */
export const journal: Action[] = [];

function record(a: Action) { journal.push(a); }

export const actions = {
  /** Neue Partie. `ver` ist die Regelversion; sie steht so im Zugprotokoll und gilt beim Nachspielen. */
  newGame(seed?: number | null, ver?: number) { setS(fresh(seed ?? undefined, ver && ver > 0 ? Math.min(ver, RULES) : 1)); journal.length = 0; }, // ohne Angabe: Version 1, wie in alten Protokollen
  setName(name: string) { S.name = name || 'Aufbruch'; },
  setProg(axis: string, v: number) { S.you[axis] = v; },
  setColor(c: string) { if (S.phase === 'program' && !S.mp && isHex(c) && !colorProblem(c, 'YOU')) S.color = c.toLowerCase(); },
  setDiff(d: string) { S.cfg = { diff: d, ...PRESETS[d] }; },
  setCfg(k: string, on: boolean) { S.cfg = { ...CF(), [k]: on }; },
  /** Regelversion 4: Detailgrad der Verwaltung (0–2). */
  setAdm(n: number) { S.cfg = { ...CF(), adm: Math.max(0, Math.min(2, Math.round(Number(n)) || 0)) }; },
  admSet(rid: string, v: number) { admSet(rid, v); },
  admInv(v: number) { admInv(v); },
  admLand(v: number) { admLand(v); },
  admOpt(k: 'beamte' | 'pay', on: boolean) { admOpt(k, on); },
  /** Regelversion 5: Kommunikation, Reaktion auf die Opposition, Koalitionsausschuss. */
  comFocus(iss: string) { comFocus(iss); },
  comLaw(id: number) { comLaw(id); },
  comTalk(kind: string) { comTalk(kind); },
  oppMove(i: number) { oppMove(i); },
  coalCommittee(i: number) { coalCommittee(i); },
  elect() { runElection(); },
  pick(id: string) { S.partners = S.partners.includes(id) ? S.partners.filter((x: string) => x !== id) : [...S.partners, id].slice(-3); versusOnPick(); },
  talks() { if (!versusTalksOk()) return; S.agree = { ...S.you }; S.ress = defaultRess(); S.phase = 'talks'; versusOnTalks(); },
  setRess(rid: string, p: string) { S.ress[rid] = p; versusContractChanged(); },
  agree(axis: string, v: number) { S.agree[axis] = v; versusContractChanged(); },
  back() { S.phase = 'night'; S.agree = null; },
  sign() { if (!versusSignOk()) return; trySign(); },
  alone() { S.partners = []; S.agree = null; S.ress = {}; startGovern(); },
  oppVote(yes: boolean) { decideOpp(yes); },
  tick() { S.event = null; aiTurn(); tick(); aiTurn(); },
  run() { runMonths(); },
  /** Mehrspieler: Spieler meldet sich bereit (oder nimmt es zurück). Sind alle bereit, läuft die Zeit bis zum nächsten wichtigen Ereignis. */
  ready(on = true) {
    if (!isVersus() || S.phase !== 'govern') return;
    const M = S.mp!; M.ready = { ...(M.ready || {}), [actor()]: !!on };
    if (ROLES.every((r) => M.ready![r]) && (M.chan === 'ai' || !blocker())) { M.ready = {}; runMonths(); }
  },
  draft(iss: string) { if (coopGov() && !issueDraftable('YOU', iss)) return; const T = TPL(iss, firstTpl('YOU', iss)); S.editing = { iss, tpl: firstTpl('YOU', iss), lawId: null, p: Object.fromEntries(T.ps.map((p: any) => [p.id, p.d])) }; },
  tpl(k: number) { const T = TPL(S.editing!.iss, k); S.editing!.tpl = k; S.editing!.p = T.custom ? customDefault(S.editing!.iss) : Object.fromEntries(T.ps.map((p: any) => [p.id, p.d])); },
  edit(lawId: number) { const L = S.laws.find((x) => x.id === lawId)!; if (!ownsLaw('YOU', L)) return; S.editing = { iss: L.iss!, tpl: L.tpl || 0, lawId: L.id, p: JSON.parse(JSON.stringify(L.p)) }; },
  param(id: string, v: number) { S.editing!.p[id] = v; },
  title(t: string) { S.editing!.p.title = t; },
  blockAdd(k: string) { const bl = S.editing!.p.blocks || (S.editing!.p.blocks = []); if (bl.length < 4) bl.push(newBlock(k)); },
  blockDel(i: number) { S.editing!.p.blocks.splice(i, 1); },
  blockSet(i: number, f: string, v: number) { S.editing!.p.blocks[i][f] = v; },
  cancel() { S.editing = null; },
  /** Regelversion 2: Gesetz zu einem Meinungsthema entwerfen, auch ohne passendes Problem auf der Liste. */
  draftAgenda(ax: string, i: number) { if (coopGov()) { const it = AGENDA[ax]?.[i]; if (!it || !canDraft('YOU', TPL(it.iss, it.k))) return; } openAgendaDraft(ax, i); },
  /** Zwischen zwei Legislaturen auf neuere Spielregeln umsteigen (nur Einzelspieler). `to` steht im Protokoll. */
  upgradeRules(to: number) { upgradeRules(to); },
  brDeal(lawId: number) { const L = S.laws.find((x) => x.id === lawId); if (L) brDeal(L); },
  commission() {
    const E = S.editing!, T = TPL(E.iss, E.tpl), fx = lawFx(T, E.p); // v1: genau T.fx(E.p)
    if (coopGov() && !canDraft('YOU', T)) return; // Ressortprinzip
    if (spendingFreeze(fx)) return; // Ausgabenstopp
    if (E.lawId) {
      const L = S.laws.find((x) => x.id === E.lawId)!;
      Object.assign(L, { p: JSON.parse(JSON.stringify(E.p)), fx, stage: 'draft', left: 1, why: null, vote: null, hear: null, deal: false, q: draftInfo(T, fx).q, br: T.br || (fx.gg ? 1 : 0), t: T.custom ? (E.p.title || T.t) : L.t });
      log(`„${L.t}“ wird überarbeitet.`);
      if (coopGov()) startCoord(L, 'YOU');
    } else {
      const di = draftInfo(T, fx);
      const L: Law = { id: S.nid++, iss: E.iss, tpl: E.tpl || 0, t: T.custom ? (E.p.title || T.t) : T.t, r: T.r, br: T.br || (fx.gg ? 1 : 0), p: JSON.parse(JSON.stringify(E.p)), fx, stage: 'draft', left: di.months, q: di.q, rid: di.rid, delay: di.delay };
      S.laws.push(L); S.issues[E.iss].law = L.id;
      if (coopGov()) startCoord(L, 'YOU'); else log(`Ministerium ${T.r} beginnt mit „${L.t}“.`);
    }
    S.editing = null;
  },
  cabinet(lawId: number) { const L = S.laws.find((x) => x.id === lawId); if (!L || L.stage !== 'ready' || !ownsLaw('YOU', L)) return; introduce(L); },
  drop(lawId: number) { const L = S.laws.find((x) => x.id === lawId); if (!L || !ownsLaw('YOU', L)) return; S.laws = S.laws.filter((x) => x !== L); if (L.iss && S.issues[L.iss]) S.issues[L.iss].law = null; log(`„${L.t}“ wird zu den Akten gelegt.`); },
  nextLeg() { (S as any).resPrev = S.res; S.res = S.res2!; S.res2 = null; S.elec = S.final; S.leg++; S.partners = []; S.agree = null; S.fin = 'YOU'; S.ress = {}; S.end = null; S.early = false; S.m += 2; S.phase = 'night'; },
  snapElection() { S.early = true; if (S.phase === 'govern') { openReview(true); return; } if (isVersus()) mpCampStart(); else startCampaign(false); },
  reviewDone() { reviewDone(); },
  reposition(a: string, v: number) { reposition(a, v); },
  selectState(id: string | null) { S.selState = S.selState === id ? null : id; },
  mapMode(m: string) { S.mapMode = m; },
  tab(t: string) { S.tab = t; },
  crisis(i: number) { decideCrisis(i); },
  decide(i: number) { decide(i); },
  ptag(accept: boolean) { decidePtag(accept); },
  repeal(lawId: number) { startRepeal(lawId); },
  budgetOpen() { S.bview = true; },
  budgetClose() { S.bview = false; },
  budgetSet(k: string, v: number) { if (k === 'ein') S.budget!.ein = v; else S.budget!.etat[k] = v; },
  budgetNot(on: boolean) { S.budget!.not = on; },
  budgetPass(force: boolean) { budgetCabinet(force); },
  budgetRetry() { budgetRetry(); },
  scandal(fire: boolean) { scandalDecide(fire); },
  appoint(i: number) { appoint(i); },
  pickCancel() { S.pickFor = null; },
  reshuffle(rid: string) { S.pickFor = { rid, why: 'umbildung', c: candidates('YOU') }; },
  campStart() { if (isVersus()) { mpCampStart(); return; } startCampaign(true); },
  theme(id: string) { const t = S.camp!.themes; S.camp!.themes = t.includes(id) ? t.filter((x: string) => x !== id) : [...t, id].slice(-3); },
  campStep(step: Campaign['step']) { const c = S.camp!; if (step !== 'themen' && c.themes.length !== 3) return; c.step = step; },
  ad(ch: string, v: number) { const c = S.camp; if (!c || !c.fund) return; c.ads = setAlloc(c.ads || {}, ch, v, c.fund.total); },
  mpAd(ch: string, v: number) { mpAd(ch, v); },
  stop(id: string) { const st = S.camp!.stops; if (stopsUsed() < STOPS && (st[id] || 0) < 3) st[id] = (st[id] || 0) + 1; },
  stopsReset() { S.camp!.stops = {}; },
  answer(i: number) { const c = S.camp!, q = c.q[c.ans.length]; if (q) { const [val, txt] = duelEffect(q.o[i]); c.ans.push({ v: val as number, txt: txt as string }); } },
  campDone() { finishCampaign(); },
  // Zwei-Spieler-Modus
  mpStart() { versusStart(); },
  oppCreate(def: { n: string; a: string; c: string; pos: Record<string, number> }) { versusCreate(def); },
  mpCreate(def: { n: string; a: string; c: string; pos: Record<string, number> }) { versusCreate(def); },
  mpCampStart() { mpCampStart(); },
  mpTheme(id: string) { mpTheme(id); },
  mpStep(step: 'themen' | 'tour' | 'werbung' | 'duell' | 'fazit') { mpStep(step); },
  mpStop(id: string) { mpStop(id); },
  mpStopsReset() { mpStopsReset(); },
  mpAnswer(i: number) { mpAnswer(i); },
  mpCampDone() { mpCampDone(); },
  mandateReturn() { mandateReturn(); },
  coalReply(ok: boolean) { coalReply(ok, aiAfterReply); },
  oppPropose(iss: string, tpl: number, p: Record<string, number>) { oppPropose(iss, tpl, p); },
  oppSue(lawId: number) { oppSue(lawId); },
  oppAttack(iss: string) { oppAttack(iss); },
  oppMisstrauen() { oppMisstrauen(); },
  partnerBlock(lawId: number) { partnerBlock(lawId); },
  partnerQuit() { partnerQuit(); },
  // Ressortabstimmung im gemeinsamen Regieren
  coordOk(lawId: number) { coordReply('YOU', lawId, 'ok'); },
  coordCounter(lawId: number, p: Record<string, number>) { coordReply('YOU', lawId, 'counter', p); },
  coordReject(lawId: number) { coordReply('YOU', lawId, 'reject'); },
  coordWithdraw(lawId: number) { coordReply('YOU', lawId, 'withdraw'); },
  p2Propose(iss: string, tpl: number, p: Record<string, number>) { p2Propose(iss, tpl, p); },
  p2CoordOk(lawId: number) { const p = myPartnerPid(); if (p) coordReply(p, lawId, 'ok'); },
  p2CoordCounter(lawId: number, q: Record<string, number>) { const p = myPartnerPid(); if (p) coordReply(p, lawId, 'counter', q); },
  p2CoordReject(lawId: number) { const p = myPartnerPid(); if (p) coordReply(p, lawId, 'reject'); },
  p2CoordWithdraw(lawId: number) { const p = myPartnerPid(); if (p) coordReply(p, lawId, 'withdraw'); },
  p2Cabinet(lawId: number) { const p = myPartnerPid(), L = S.laws.find((x) => x.id === lawId); if (p && L && L.stage === 'ready' && ownsLaw(p, L)) introduce(L); },
  p2Drop(lawId: number) { const p = myPartnerPid(), L = S.laws.find((x) => x.id === lawId); if (!p || !L || !ownsLaw(p, L) || !['failed', 'blocked'].includes(L.stage)) return; S.laws = S.laws.filter((x) => x !== L); if (L.iss && S.issues[L.iss]) S.issues[L.iss].law = null; log(`„${L.t}“ wird zu den Akten gelegt.`); },
  p2Wish(axis: string, v: number) { p2Wish(axis, v); },
  p2RessWish(rid: string) { p2RessWish(rid); },
  p2Vote(lawId: number, yes: boolean) { p2Vote(lawId, yes); },
  p2Plan(iss: string | null) { p2Plan(iss); },
};

export type ActionName = keyof typeof actions;

/** Nur Ansicht, kein Spielzustand: werden im Mehrspieler nicht synchronisiert. */
export const LOCAL_ACTIONS: ReadonlySet<string> = new Set(['tab', 'mapMode', 'selectState']);
/** Züge eines Spielers für seine eigene Partei (Opposition, Partner, Wahlkampf). Prüfen selbst, wer handelt. */
export const PLAYER_ACTIONS: ReadonlySet<string> = new Set(['oppCreate', 'mpCreate', 'coalReply', 'oppPropose', 'oppSue', 'oppAttack', 'oppMisstrauen', 'partnerBlock', 'partnerQuit', 'p2Vote', 'p2Wish', 'p2RessWish', 'p2Propose', 'p2CoordOk', 'p2CoordCounter', 'p2CoordReject', 'p2CoordWithdraw', 'p2Cabinet', 'p2Drop', 'mpTheme', 'mpStep', 'mpStop', 'mpStopsReset', 'mpAnswer', 'mpAd', 'mpCampDone', 'reposition', 'ready', 'p2Plan']);
/** Zeitsteuerung: der Kanzler-Spieler, sonst der Host. */
export const TIME_ACTIONS: ReadonlySet<string> = new Set(['tick', 'run', 'snapElection', 'reviewDone', 'mpCampStart', 'campStart', 'setDiff', 'setCfg']);
/** Kompatibilität: frühere Bezeichnung. */
export const OPP_ACTIONS = PLAYER_ACTIONS;

/** Darf der handelnde Spieler diesen Zug machen? Im Einzelspieler immer. */
export function allowedFor(name: string, who: Role): boolean {
  if (!isVersus()) return true;
  if (name === 'newGame' || name === 'mpStart' || LOCAL_ACTIONS.has(name) || PLAYER_ACTIONS.has(name)) return true;
  const M = S.mp!;
  if (TIME_ACTIONS.has(name)) return who === timeRole() || M.chan === who;
  return M.chan === who; // alle übrigen Züge gehören dem Kanzler-Spieler
}

/** Führt eine Aktion aus und protokolliert sie. `as` ist der handelnde Spieler (Mehrspieler). */
export function dispatch<K extends ActionName>(name: K, ...args: Parameters<(typeof actions)[K]>) {
  dispatchAs(actor(), name as string, args as unknown[]);
}
export function dispatchAs(who: Role, name: string, args: unknown[]) {
  setActor(who);
  if (!(name in actions) || !allowedFor(name, who)) return;
  (actions[name as ActionName] as (...x: unknown[]) => void)(...args);
  record({ type: name, args, as: who });
  if (S) S.rng = rngState();
}


/** Macht einen gespeicherten Zustand mit älteren Feldern lauffähig. */
export function migrate(s: any) {
  s.confirmNew = false; s.mp ??= null; s.ver ??= 1; // Spielstände von vor der Versionierung laufen nach Regeln v1
  if (s.mp && s.mp.mode === 'versus' && !s.mp.pl) s.mp = null; // Mehrspieler-Stand im alten Format s.news ??= []; s.press ??= 0; s.oppBill ??= null;
  s.leg ??= 0; s.m0 ??= 0; s.career ??= []; s.elYear ??= 2029; s.legYear ??= 2029; s.mapMode ??= 'mood';
  setS(s);
  if (!s.gov) Object.assign(s, initStates());
  s.etat ??= {}; s.ein ??= 0; s.budgets ??= []; s.promises ??= []; s.tab ??= 'lage'; s.scandalsLeg ??= 0;
  s.ress ??= {}; s.ind ??= {}; s.ind0 ??= {}; s.hist ??= {};
  if (s.phase === 'talks' && !Object.keys(s.ress).length) s.ress = defaultRess();
  if (s.phase === 'govern' && !s.min) { if (!Object.keys(s.ress).length) { s.ress = defaultRess(); s.ress.fin = s.fin || 'YOU'; } formCabinet(); }
  if (Object.keys(s.issues || {}).length) (verOf(s) >= 3 ? allIssues() : ISSUES).forEach((i: any) => { if (!s.issues[i.id]) s.issues[i.id] = { sev: i.sev, prev: i.sev, cut: 0, law: null, on: i.at != null && s.m >= i.at }; });
  s.cfg ??= { diff: 'normal', ...PRESETS.normal }; s.infl ??= 2.1; s.zins ??= 2.1; s.drate ??= 2; s.demoCost ??= 0; s.demoCut ??= 0;
  s.lob ??= { gew: 50, arbg: 50, soz: 50, umw: 50 }; s.wing ??= { soz: 60, wirt: 60 }; s.suits ??= []; s.ptagLeg ??= -1; s.crisisHist ??= []; s.dec ??= null; s.decHist ??= []; s.review ??= null; s.zg ??= {}; s.legStart ??= null; s.showCfg = false;
  s.lte ??= []; allAxes().forEach((a: string) => { s.you[a] ??= 0; if (s.agree) s.agree[a] ??= s.you[a]; });
  if (s.issues && Object.keys(s.issues).length && s.mood && Object.keys(s.mood).length) { if (!Object.keys(s.ind).length) initInd(); if (!Object.keys(s.ind0).length) s.ind0 = { ...s.ind }; addBestand(); }
  if (s.rng) restoreRng(s.rng);
  return s;
}

export { S, isWon };
